import React, { useEffect, useRef, useState } from 'react';
import { FaBell, FaPlus, FaTrash, FaEdit, FaList, FaCalendarAlt, FaRedo } from 'react-icons/fa';
import api from '../api';

const KEY = 'finance_expenses_v1';
const RECURRENCE_KEY = 'finance_expense_recurrence_patterns_v1';
const AUDIENCE_KEY = 'finance_expense_default_audience_v1';
const REMINDER_LOG_KEY = 'finance_expense_reminder_log_v1';

const defaultRecurrencePatterns = [
  { id: 'monthly', label: 'Todo mês', interval: 1, unit: 'months' },
  { id: 'day10', label: 'Todo dia 10', interval: 10, unit: 'days' },
  { id: 'day20', label: 'Todo dia 20', interval: 20, unit: 'days' },
  { id: 'each30days', label: 'A cada 30 dias', interval: 30, unit: 'days' },
];

const parseDateValue = (value) => {
  if (value instanceof Date) return new Date(value);
  const raw = String(value || '').trim();
  if (!raw) return new Date(NaN);
  const brMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) {
    const [, dd, mm, yyyy] = brMatch;
    return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  }
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return new Date(`${raw}T00:00:00`);
  }
  return new Date(raw);
};

const toPeriodDays = (interval, unit) => {
  if (unit === 'days') return interval;
  if (unit === 'weeks') return interval * 7;
  if (unit === 'months') return interval * 30;
  if (unit === 'years') return interval * 365;
  return interval;
};

const addIntervalToDate = (baseDate, interval, unit) => {
  const d = parseDateValue(baseDate);
  if (Number.isNaN(d.getTime())) return d;
  if (unit === 'days') d.setDate(d.getDate() + interval);
  if (unit === 'weeks') d.setDate(d.getDate() + interval * 7);
  if (unit === 'months') d.setMonth(d.getMonth() + interval);
  if (unit === 'years') d.setFullYear(d.getFullYear() + interval);
  return d;
};

const toDateOnly = (date) => {
  const d = parseDateValue(date);
  if (Number.isNaN(d.getTime())) return d;
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatDateOnly = (date) => toDateOnly(date).toISOString().slice(0, 10);

const createEmpty = () => ({
  id: null,
  title: '',
  amount: '',
  type: 'planned',
  mode: 'fixed',
  fixedDate: '',
  recurrencePatternId: defaultRecurrencePatterns[0].id,
  recurrenceInterval: 1,
  recurrenceUnit: 'months',
  recurrenceStartDate: '',
  defaultAudience: false,
  targetUserIds: [],
  targetSectorIds: [],
});

export default function ExpensesManager({ canManage }) {
  const [tab, setTab] = useState('expenses');
  const [subTab, setSubTab] = useState('form');
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(createEmpty());
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [recurrencePatterns, setRecurrencePatterns] = useState(defaultRecurrencePatterns);
  const [draftPatterns, setDraftPatterns] = useState(defaultRecurrencePatterns);
  const [defaultRecipients, setDefaultRecipients] = useState({ userIds: [], sectorIds: [] });
  const syncingRemindersRef = useRef(false);

  const createReminderIfNeeded = async (entry, reminderDate, dueDate, existingReminders) => {
    const reminderDay = formatDateOnly(reminderDate);
    const dueDay = formatDateOnly(dueDate);
    const today = formatDateOnly(new Date());
    if (reminderDay !== today) return;
    const log = JSON.parse(localStorage.getItem(REMINDER_LOG_KEY) || '{}');
    const key = `${entry.id}_${dueDay}_${reminderDay}`;
    if (log[key]) return;
    const signature = `${entry.title}__${reminderDay}__${dueDay}`;
    if (existingReminders?.has(signature)) {
      log[key] = true;
      localStorage.setItem(REMINDER_LOG_KEY, JSON.stringify(log));
      return;
    }
    const targetUserIds = entry.defaultAudience ? defaultRecipients.userIds : (entry.targetUserIds || []);
    const targetSectorIds = entry.defaultAudience ? defaultRecipients.sectorIds : (entry.targetSectorIds || []);
    await api.post('/calendar/events', {
      title: `Lembrete despesa: ${entry.title}`,
      description: `Valor previsto: R$ ${entry.amount}. Vencimento: ${dueDay}.`,
      date: reminderDay,
      notifyOnDashboard: true,
      sendToAll: false,
      targetUserIds,
      targetSectorIds,
    }).catch(() => {});
    if (existingReminders) existingReminders.add(signature);
    log[key] = true;
    localStorage.setItem(REMINDER_LOG_KEY, JSON.stringify(log));
  };

  const syncRecurringRemindersForToday = async (entries) => {
    const today = toDateOnly(new Date());
    let existingReminders = new Set();
    try {
      const summary = await api.get('/calendar/summary');
      const events = Array.isArray(summary.data?.events) ? summary.data.events : [];
      existingReminders = new Set(events
        .filter((event) => (event?.title || '').startsWith('Lembrete despesa:'))
        .map((event) => {
          const title = String(event?.title || '').replace('Lembrete despesa: ', '').trim();
          const eventDay = formatDateOnly(event?.date);
          const dueMatch = String(event?.description || '').match(/Vencimento:\s*(\d{4}-\d{2}-\d{2})/);
          const dueDay = dueMatch?.[1] || '';
          return `${title}__${eventDay}__${dueDay}`;
        }));
    } catch {}
    const recurring = (entries || []).filter((item) => item?.mode === 'recurrence' && item?.recurrenceStartDate);
    for (const entry of recurring) {
      const periodDays = toPeriodDays(entry.recurrenceInterval, entry.recurrenceUnit);
      const remindDaysBefore = periodDays <= 3 ? 0 : 2;
      for (let index = 0; index < 24; index += 1) {
        const dueDate = addIntervalToDate(entry.recurrenceStartDate, entry.recurrenceInterval * index, entry.recurrenceUnit);
        if (Number.isNaN(dueDate.getTime())) break;
        const normalizedDueDate = toDateOnly(dueDate);
        if (normalizedDueDate < today) continue;
        await createReminderIfNeeded(entry, normalizedDueDate, normalizedDueDate, existingReminders);
        if (remindDaysBefore > 0) {
          const earlyReminder = new Date(normalizedDueDate);
          earlyReminder.setDate(earlyReminder.getDate() - remindDaysBefore);
          await createReminderIfNeeded(entry, earlyReminder, normalizedDueDate, existingReminders);
        }
        break;
      }
    }
  };

  useEffect(() => {
    try { setItems(JSON.parse(localStorage.getItem(KEY) || '[]')); } catch { setItems([]); }
    try {
      const stored = JSON.parse(localStorage.getItem(RECURRENCE_KEY) || '[]');
      if (Array.isArray(stored) && stored.length) {
        setRecurrencePatterns(stored);
        setDraftPatterns(stored);
      }
    } catch {}
    try {
      const audience = JSON.parse(localStorage.getItem(AUDIENCE_KEY) || '{}');
      setDefaultRecipients({
        userIds: Array.isArray(audience?.userIds) ? audience.userIds : [],
        sectorIds: Array.isArray(audience?.sectorIds) ? audience.sectorIds : [],
      });
    } catch {}
    api.get('/calendar/recipients').then((r) => {
      setUsers(Array.isArray(r.data?.users) ? r.data.users : []);
      setSectors(Array.isArray(r.data?.sectors) ? r.data.sectors : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const runSync = async () => {
      if (syncingRemindersRef.current) return;
      syncingRemindersRef.current = true;
      try {
        await syncRecurringRemindersForToday(items);
      } finally {
        syncingRemindersRef.current = false;
      }
    };
    runSync();
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = (next) => {
    setItems(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const persistPatterns = (next) => {
    setRecurrencePatterns(next);
    setDraftPatterns(next);
    localStorage.setItem(RECURRENCE_KEY, JSON.stringify(next));
  };
  const saveAudience = (next) => {
    setDefaultRecipients(next);
    localStorage.setItem(AUDIENCE_KEY, JSON.stringify(next));
  };

  const save = async (e) => {
    e.preventDefault();
    const selectedPattern = recurrencePatterns.find((p) => p.id === form.recurrencePatternId);
    const recurrenceDescription = form.mode === 'recurrence'
      ? `${form.recurrenceInterval} ${form.recurrenceUnit}`
      : '';
    const entry = {
      ...form,
      id: form.id || Date.now(),
      recurrence: recurrenceDescription,
      recurrenceLabel: selectedPattern?.label || recurrenceDescription,
    };
    const next = form.id ? items.map((x) => (x.id === form.id ? entry : x)) : [entry, ...items];
    persist(next);
    const basePayload = {
      notifyOnDashboard: true,
      sendToAll: false,
      targetUserIds: entry.defaultAudience ? defaultRecipients.userIds : entry.targetUserIds,
      targetSectorIds: entry.defaultAudience ? defaultRecipients.sectorIds : entry.targetSectorIds,
    };
    if (entry.mode === 'fixed' && entry.fixedDate) {
      await api.post('/calendar/events', {
        title: `Despesa: ${entry.title}`,
        description: `Valor previsto: R$ ${entry.amount}`,
        date: entry.fixedDate,
        ...basePayload,
      }).catch(() => {});
    }
    if (entry.mode === 'recurrence' && entry.recurrenceStartDate) {
      await syncRecurringRemindersForToday(next);
    }
    setForm(createEmpty());
    setSubTab('list');
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-700 via-teal-600 to-cyan-600 p-5 text-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-50"><FaBell /> Financeiro</span>
            <h3 className="mt-3 text-2xl font-bold">Lembrete de despesas</h3>
            <p className="mt-1 max-w-2xl text-sm text-emerald-50">Cadastre vencimentos, recorrências e públicos de notificação em um painel simples para não perder prazos.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-right sm:grid-cols-3">
            <div className="rounded-2xl bg-white/15 px-4 py-3"><p className="text-xs text-emerald-50">Lembretes</p><p className="text-2xl font-bold leading-none">{items.length}</p></div>
            <div className="rounded-2xl bg-white/15 px-4 py-3"><p className="text-xs text-emerald-50">Fixos</p><p className="text-2xl font-bold leading-none">{items.filter((item) => item.mode === 'fixed').length}</p></div>
            <div className="rounded-2xl bg-white/15 px-4 py-3"><p className="text-xs text-emerald-50">Recorrentes</p><p className="text-2xl font-bold leading-none">{items.filter((item) => item.mode === 'recurrence').length}</p></div>
          </div>
        </div>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-2">
        <button className={`rounded-2xl border px-4 py-4 text-left transition ${tab === 'expenses' ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm' : 'border-emerald-100 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'}`} onClick={() => setTab('expenses')}>
          <p className="flex items-center gap-2 font-semibold"><FaCalendarAlt /> Lembretes</p>
          <p className={`mt-1 text-xs ${tab === 'expenses' ? 'text-emerald-100' : 'text-emerald-700'}`}>Cadastre, edite e acompanhe vencimentos</p>
        </button>
        <button className={`rounded-2xl border px-4 py-4 text-left transition ${tab === 'recurring' ? 'border-cyan-600 bg-cyan-600 text-white shadow-sm' : 'border-cyan-100 bg-cyan-50 text-cyan-800 hover:bg-cyan-100'}`} onClick={() => setTab('recurring')}>
          <p className="flex items-center gap-2 font-semibold"><FaRedo /> Padrões de recorrência</p>
          <p className={`mt-1 text-xs ${tab === 'recurring' ? 'text-cyan-100' : 'text-cyan-700'}`}>Configure repetições e notificações</p>
        </button>
      </div>
      {tab === 'expenses' && (
        <>
          <div className="mb-3 grid gap-2 md:grid-cols-2">
            {canManage && <button className={`rounded-xl px-3 py-2 text-left ${subTab === 'form' ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`} onClick={() => setSubTab('form')}><FaPlus className="mr-2 inline"/> Cadastrar lembrete</button>}
            <button className={`rounded-xl px-3 py-2 text-left ${subTab === 'list' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} onClick={() => { setSubTab('list'); setForm(createEmpty()); }}><FaList className="mr-2 inline"/> Listagem</button>
          </div>
          {subTab === 'form' ? <form onSubmit={save} className="grid gap-2 md:grid-cols-2">
            <input required placeholder="Nome da despesa" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="rounded border p-2"/>
            <input required placeholder="Valor" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} className="rounded border p-2"/>
            <select value={form.type} onChange={(e)=>setForm({...form,type:e.target.value})} className="rounded border p-2"><option value="planned">Prevista</option><option value="recurring">Recorrente</option></select>
            <select value={form.mode} onChange={(e)=>setForm({...form,mode:e.target.value})} className="rounded border p-2"><option value="fixed">Data fixa</option><option value="recurrence">Recorrência</option></select>
            {form.mode === 'fixed' ? <input type="date" value={form.fixedDate} onChange={(e)=>setForm({...form,fixedDate:e.target.value})} className="rounded border p-2"/> : <>
              <input required type="date" value={form.recurrenceStartDate} onChange={(e)=>setForm({...form,recurrenceStartDate:e.target.value})} className="rounded border p-2" />
              <select value={form.recurrencePatternId} onChange={(e)=>{
                const selected = recurrencePatterns.find((p) => p.id === e.target.value);
                setForm({
                  ...form,
                  recurrencePatternId:e.target.value,
                  recurrenceInterval:selected?.interval || 1,
                  recurrenceUnit:selected?.unit || 'months',
                });
              }} className="rounded border p-2">
                {recurrencePatterns.map((op)=><option key={op.id} value={op.id}>{op.label}</option>)}
              </select>
              <div className="flex gap-2">
                <input type="number" min="1" value={form.recurrenceInterval} onChange={(e)=>setForm({...form,recurrenceInterval:Number(e.target.value || 1)})} className="w-1/2 rounded border p-2"/>
                <select value={form.recurrenceUnit} onChange={(e)=>setForm({...form,recurrenceUnit:e.target.value})} className="w-1/2 rounded border p-2">
                  <option value="days">Dias</option><option value="weeks">Semanas</option><option value="months">Meses</option><option value="years">Anos</option>
                </select>
              </div>
            </>}
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.defaultAudience} onChange={(e)=>setForm({...form,defaultAudience:e.target.checked})}/> Usar público padrão (usuários e setores)</label>
            {!form.defaultAudience && <>
              <select multiple value={form.targetSectorIds} onChange={(e)=>setForm({...form,targetSectorIds:Array.from(e.target.selectedOptions).map(o=>Number(o.value))})} className="rounded border p-2">{sectors.map((s)=><option key={s.id} value={s.id}>{s.name}</option>)}</select>
              <select multiple value={form.targetUserIds} onChange={(e)=>setForm({...form,targetUserIds:Array.from(e.target.selectedOptions).map(o=>Number(o.value))})} className="rounded border p-2">{users.map((u)=><option key={u.id} value={u.id}>{u.name}</option>)}</select>
            </>}
            <button disabled={!canManage} className="rounded bg-emerald-600 p-2 text-white">Salvar lembrete</button>
          </form> : <div className="space-y-2">
            {items.map((it)=><div key={it.id} className="flex items-center justify-between rounded border p-2"><div><div className="font-medium">{it.title}</div><div className="text-xs text-slate-500">{it.mode === 'fixed' ? `Data: ${it.fixedDate || '-'}` : `Recorrência: ${it.recurrenceLabel || it.recurrence}`}</div></div><div className="flex gap-2">{canManage && <button onClick={()=>{setForm(it);setSubTab('form');}}><FaEdit/></button>}{canManage && <button onClick={()=>persist(items.filter((x)=>x.id!==it.id))}><FaTrash/></button>}</div></div>)}
          </div>}
        </>
      )}
      {tab === 'recurring' && <div>
        <h4 className="mb-2 font-semibold">Padrões de recorrência</h4>
        <p className="text-sm text-slate-600">Você pode selecionar, editar ou remover os padrões abaixo.</p>
        <div className="mt-2 space-y-2">
          {draftPatterns.map((pattern) => (
            <div key={pattern.id} className="grid gap-2 rounded border p-2 md:grid-cols-5">
              <input value={pattern.label} onChange={(e)=>setDraftPatterns(draftPatterns.map((x)=>x.id===pattern.id?{...x,label:e.target.value}:x))} className="rounded border p-2 md:col-span-2" />
              <input type="number" min="1" value={pattern.interval} onChange={(e)=>setDraftPatterns(draftPatterns.map((x)=>x.id===pattern.id?{...x,interval:Number(e.target.value || 1)}:x))} className="rounded border p-2" />
              <select value={pattern.unit} onChange={(e)=>setDraftPatterns(draftPatterns.map((x)=>x.id===pattern.id?{...x,unit:e.target.value}:x))} className="rounded border p-2">
                <option value="days">Dias</option><option value="weeks">Semanas</option><option value="months">Meses</option><option value="years">Anos</option>
              </select>
              <button className="rounded bg-rose-50 px-2 py-1 text-rose-600" onClick={()=>setDraftPatterns(draftPatterns.filter((x)=>x.id!==pattern.id))}><FaTrash className="inline"/> Remover</button>
            </div>
          ))}
          <div className="flex gap-2">
            <button className="rounded bg-slate-100 px-3 py-2" onClick={() => setDraftPatterns([...draftPatterns, { id: `custom_${Date.now()}`, label: 'Novo padrão', interval: 1, unit: 'months' }])}><FaPlus className="inline"/> Novo padrão</button>
            <button className="rounded bg-emerald-600 px-3 py-2 text-white" onClick={() => persistPatterns(draftPatterns)}>Salvar padrões</button>
          </div>
        </div>
        <h4 className="mb-2 mt-4 font-semibold">Público padrão</h4>
        <div className="mt-2 grid gap-3 md:grid-cols-2">
          <select multiple value={defaultRecipients.sectorIds} onChange={(e)=>setDefaultRecipients({...defaultRecipients,sectorIds:Array.from(e.target.selectedOptions).map(o=>Number(o.value))})} className="rounded border p-2">
            {sectors.map((s)=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select multiple value={defaultRecipients.userIds} onChange={(e)=>setDefaultRecipients({...defaultRecipients,userIds:Array.from(e.target.selectedOptions).map(o=>Number(o.value))})} className="rounded border p-2">
            {users.map((u)=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <button className="mt-3 rounded bg-emerald-600 px-3 py-2 text-white" onClick={() => saveAudience(defaultRecipients)}><FaBell className="mr-1 inline"/> Salvar público padrão</button>
      </div>}
    </section>
  );
}
